import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { FetchError } from 'src/types/TApiQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { queryNames } from 'src/api/utils/queryNames';
import {
  createSchedule,
  updateSchedule,
  deleteSchedule,
  issueScheduleCredential,
  revokeScheduleCredentials,
} from 'src/api/functions/schedulesApi';
import type {
  ScheduleDefinition,
  CreateSchedulePayload,
  UpdateSchedulePayload,
  CredentialStatus,
  CredentialIssuePayload,
} from 'src/types/Schedules';

export function useCreateScheduleMutation(
  mutationOptions?: UseMutationOptions<ScheduleDefinition, FetchError, CreateSchedulePayload>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: CreateSchedulePayload) => createSchedule(accessToken, payload)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.schedules]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useUpdateScheduleMutation(
  scheduleId: string,
  mutationOptions?: UseMutationOptions<ScheduleDefinition, FetchError, UpdateSchedulePayload>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: UpdateSchedulePayload) => updateSchedule(accessToken, scheduleId, payload)),
    [accessToken, scheduleId],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.schedules]);
      await queryClient.invalidateQueries([queryNames.schedule, scheduleId]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useDeleteScheduleMutation(
  mutationOptions?: UseMutationOptions<void, FetchError, string>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((id: string) => deleteSchedule(accessToken, id)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.schedules]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useIssueCredentialMutation(
  mutationOptions?: UseMutationOptions<CredentialStatus, FetchError, CredentialIssuePayload>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: CredentialIssuePayload) => issueScheduleCredential(accessToken, payload)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.scheduleCredentials]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useRevokeCredentialsMutation(
  mutationOptions?: UseMutationOptions<void, FetchError, void>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(() => revokeScheduleCredentials(accessToken)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.scheduleCredentials]);
      await queryClient.invalidateQueries([queryNames.schedules]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}
